import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Text } from '@mantine/core';

// Vocabulary tags of an art, grouped by dimension, one color per dimension.
// Tags come from the backend as { dimension: [{ value, ru }] } (art.artTags);
// ru is the Russian wording, shown when the UI language is Russian.
export const DIMENSIONS = [
  { key: 'genre',     color: 'blue',   ru: 'Жанр',       en: 'Genre' },
  { key: 'subgenre',  color: 'indigo', ru: 'Поджанр',    en: 'Subgenre' },
  { key: 'motifs',    color: 'grape',  ru: 'Мотивы',     en: 'Motifs' },
  { key: 'tone',      color: 'pink',   ru: 'Тон',        en: 'Tone' },
  { key: 'structure', color: 'teal',   ru: 'Структура',  en: 'Structure' },
  { key: 'setting',   color: 'green',  ru: 'Место',      en: 'Setting' },
  { key: 'hero',      color: 'orange', ru: 'Герой',      en: 'Hero' },
  { key: 'format',    color: 'gray',   ru: 'Формат',     en: 'Format' },
  { key: 'origin',    color: 'cyan',   ru: 'Страна',     en: 'Origin' },
  { key: 'audience',  color: 'lime',   ru: 'Аудитория',  en: 'Audience' },
  { key: 'style',     color: 'violet', ru: 'Стиль',      en: 'Style' },
  { key: 'register',  color: 'dark',   ru: 'Ниша',       en: 'Register' },
];

// People features used by the top-todos ranker alongside vocabulary tags.
export const DIMENSION_COLORS = Object.fromEntries(DIMENSIONS.map(d => [d.key, d.color]));
DIMENSION_COLORS.creator = 'red';
DIMENSION_COLORS.actor = 'yellow';
DIMENSION_COLORS.era = 'gray';

export const dimensionLabel = (key, language) => {
  const d = DIMENSIONS.find(x => x.key === key);
  return d ? (language === 'ru' ? d.ru : d.en) : key;
};

// Search-page URL listing the arts that carry these tags (whole family by default).
export const tagSearchPath = (tags, family = true) => {
  const list = Array.isArray(tags) ? tags : [tags];
  const p = new URLSearchParams();
  list.forEach(t => p.append('tag', `${t.dimension}:${t.value}`));
  p.set('family', family ? '1' : '0');
  return `/addEvent?${p.toString()}`;
};

// Dimensions shown by default; the rest live behind a "show all tags"
// toggle so a tag-rich art doesn't bury the page in 12 rows of chips
// before ratings, cast, description. These three are the ones a reader
// typically scans first (what KIND is this).
const PRIMARY_KEYS = new Set(['genre', 'subgenre', 'tone']);

const ArtTags = ({ tags, language = 'ru' }) => {
  const [expanded, setExpanded] = useState(false);
  if (!tags) return null;
  const groups = DIMENSIONS.filter(d => (tags[d.key] || []).length > 0);
  if (groups.length === 0) return null;
  const primary = groups.filter(d => PRIMARY_KEYS.has(d.key));
  const secondary = groups.filter(d => !PRIMARY_KEYS.has(d.key));
  const visible = expanded ? groups : primary;
  const hasSecondary = secondary.length > 0;
  const renderGroup = (d) => (
    <div key={d.key} className="flex flex-wrap items-center justify-center gap-1">
      <Text size="xs" c="dimmed" className="mr-1">{language === 'ru' ? d.ru : d.en}:</Text>
      {tags[d.key].map(t => (
        <Badge key={t.value} component={Link} to={tagSearchPath({ dimension: d.key, value: t.value })}
          color={d.color} variant="light" size="sm" radius="sm" style={{ textTransform: 'none', cursor: 'pointer' }}>
          {language === 'ru' && t.ru ? t.ru : t.value}
        </Badge>
      ))}
    </div>
  );
  return (
    <div className="mx-auto w-full max-w-3xl mt-3 flex flex-col items-center gap-1">
      {visible.map(renderGroup)}
      {hasSecondary && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="mt-1 text-xs cursor-pointer"
          style={{
            background: 'transparent',
            border: 'none',
            padding: '2px 8px',
            color: 'var(--mantine-color-dimmed)',
            textDecoration: 'underline',
          }}
        >
          {expanded
            ? (language === 'ru' ? 'Свернуть теги' : 'Hide tags')
            : (language === 'ru' ? `Показать ещё теги (${secondary.length})` : `Show more tags (${secondary.length})`)}
        </button>
      )}
    </div>
  );
};

export default ArtTags;
